import React from 'react';
import { Link } from 'react-router-dom';

const WiziBoardPreviewSection: React.FC = () => {
  return (
    <div className="container-fluid py-5 bg-light">
      <div className="container py-5">
        <div className="row g-5 align-items-center">
          <div className="col-lg-6 wow fadeInLeft" data-wow-delay="0.1s">
            <div className="h-100">
              <h4 className="text-primary mb-3">WiziBoard Dashboard</h4>
              <h1 className="display-4 mb-4">
                Your Smart <span className="wiziai-gradient-text">Performance Dashboard</span>
              </h1>
              <p className="mb-4 fs-5">
                Track your progress, identify knowledge gaps, and get personalized recommendations 
                with WiziBoard - your intelligent learning companion that transforms data into actionable insights.
              </p>
              
              <div className="row g-4 mb-4">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <span className="fas fa-chart-line fa-2x me-3 text-primary"></span>
                    <div>
                      <h6 className="mb-1">Performance Trends</h6>
                      <small className="text-muted">Track score improvements</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <span className="fas fa-bullseye fa-2x me-3 text-primary"></span>
                    <div>
                      <h6 className="mb-1">Gap Analysis</h6>
                      <small className="text-muted">Identify weak areas</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <span className="fas fa-lightbulb fa-2x me-3 text-primary"></span>
                    <div>
                      <h6 className="mb-1">Smart Recommendations</h6>
                      <small className="text-muted">AI-powered study tips</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <span className="fas fa-calendar-check fa-2x me-3 text-primary"></span>
                    <div>
                      <h6 className="mb-1">Study Schedule</h6>
                      <small className="text-muted">Personalized timeline</small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-md-row gap-3">
                <Link to="/dashboard" className="btn btn-primary btn-lg px-4 py-3">
                  <i className="fas fa-tachometer-alt me-2"></i>Explore Your WiziBoard
                </Link>
                <Link to="/login" className="btn btn-outline-primary btn-lg px-4 py-3">
                  <i className="fas fa-sign-in-alt me-2"></i>Sign In to View Progress
                </Link>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 wow fadeInRight" data-wow-delay="0.3s">
            <div className="position-relative">
              {/* Mock Dashboard Preview */}
              <div className="bg-white rounded-4 shadow-lg p-4 position-relative overflow-hidden">
                {/* Dashboard Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h5 className="mb-1">Welcome back, Alex!</h5>
                    <small className="text-muted">Your learning progress today</small>
                  </div>
                  <div className="bg-primary bg-opacity-10 rounded-3 px-3 py-2">
                    <span className="text-white fw-bold">JEE 2025</span>
                  </div>
                </div>

                {/* Performance Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <div className="bg-primary bg-opacity-10 rounded-3 p-3 text-center">
                      <div className="text-white fs-3 fw-bold">78%</div>
                      <small className="text-white">Overall Score</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="bg-success bg-opacity-10 rounded-3 p-3 text-center">
                      <div className="text-white fs-3 fw-bold">+12%</div>
                      <small className="text-white">This Week</small>
                    </div>
                  </div>
                </div>

                {/* Progress Chart Mockup */}
                <div className="mb-4">
                  <h6 className="mb-3">Performance Trend</h6>
                  <div className="bg-light rounded-3 p-3 position-relative" style={{ height: '120px' }}>
                    <svg className="w-100 h-100" viewBox="0 0 300 80">
                      <defs>
                        <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#667eea" stopOpacity="0.3"/>
                          <stop offset="100%" stopColor="#667eea" stopOpacity="0.1"/>
                        </linearGradient>
                      </defs>
                      <path d="M0,60 Q50,45 100,50 T200,30 T300,25" stroke="#667eea" strokeWidth="3" fill="none"/>
                      <path d="M0,60 Q50,45 100,50 T200,30 T300,25 L300,80 L0,80 Z" fill="url(#chartGradient)"/>
                      {/* Data points */}
                      <circle cx="50" cy="45" r="4" fill="#667eea"/>
                      <circle cx="150" cy="40" r="4" fill="#667eea"/>
                      <circle cx="250" cy="30" r="4" fill="#667eea"/>
                    </svg>
                  </div>
                </div>

                {/* Subject Breakdown */}
                <div className="mb-4">
                  <h6 className="mb-3">Subject Performance</h6>
                  <div className="space-y-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Physics</small>
                      <div className="flex-grow-1 mx-3">
                        <div className="bg-light rounded-pill" style={{ height: '6px' }}>
                          <div className="bg-primary rounded-pill" style={{ height: '6px', width: '85%' }}></div>
                        </div>
                      </div>
                      <small className="fw-bold text-primary">85%</small>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Chemistry</small>
                      <div className="flex-grow-1 mx-3">
                        <div className="bg-light rounded-pill" style={{ height: '6px' }}>
                          <div className="bg-warning rounded-pill" style={{ height: '6px', width: '72%' }}></div>
                        </div>
                      </div>
                      <small className="fw-bold text-warning">72%</small>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Mathematics</small>
                      <div className="flex-grow-1 mx-3">
                        <div className="bg-light rounded-pill" style={{ height: '6px' }}>
                          <div className="bg-success rounded-pill" style={{ height: '6px', width: '91%' }}></div>
                        </div>
                      </div>
                      <small className="fw-bold text-success">91%</small>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-light rounded-3 p-3">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-lightbulb text-warning me-2"></i>
                    <small className="fw-bold">Today's Recommendation</small>
                  </div>
                  <small className="text-muted">Focus on Organic Chemistry - Practice mechanism problems</small>
                </div>

                {/* Floating Elements */}
                <div className="position-absolute top-0 end-0 translate-middle">
                  <div className="bg-success rounded-circle d-flex align-items-center justify-content-center" 
                       style={{ width: '40px', height: '40px' }}>
                    <i className="fas fa-trophy text-white"></i>
                  </div>
                </div>
                
                <div className="position-absolute bottom-0 start-0 translate-middle">
                  <div className="bg-primary bg-opacity-20 rounded-circle" 
                       style={{ width: '60px', height: '60px' }}>
                  </div>
                </div>
              </div>
              
              {/* Background decorative elements */}
              <div className="position-absolute top-0 start-0 translate-middle opacity-50">
                <div className="bg-primary rounded-circle" style={{ width: '20px', height: '20px' }}></div>
              </div>
              <div className="position-absolute bottom-0 end-0 translate-middle opacity-50">
                <div className="bg-success rounded-circle" style={{ width: '30px', height: '30px' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiziBoardPreviewSection;
